import { Add, Close, Person } from "@mui/icons-material";
import { Box, capitalize, Divider, IconButton, Modal, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Table, usePagination } from "@frontend/features/common/components/Table";
import { ModalBox } from "@frontend/features/common/utils/modals";
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { useDebouncedState } from "@frontend/features/common/hooks/useDebouncedState";

import { CreateClientModal } from "../CreateClient";
import { useGetClientsByAgency } from "../../queries";

export interface ClientSelectorTableItem {
  name: string,
  id: string,
  agency: string,
  phone: string,
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (clientTableItem: ClientSelectorTableItem) => void;
  agencyId: number;
  selectedItemId?: number | string;
}

export const ClientSelectorModal = ({ open, onClose, onSelect, agencyId, selectedItemId }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useDebouncedState('', 500);;
  const [createClientmodal, setCreateClientModal] = useState<boolean>(false);
  const pagination = usePagination();

  const toggleCreate = () => setCreateClientModal(prev => !prev);

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  const { data: clients, isLoading } = useGetClientsByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    agencyId: agencyId.toString(),
  });

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="client-modal-selector-title">
      <ModalBox display="flex" flexDirection="column" gap={theme.spacing(2)}>
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Person color="disabled" />
            <Typography id="client-modal-selector-title" variant="h6" component="h2">
              {t('clients.selector.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider />


        <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
          <Typography
            variant="subtitle1"
            align="left"
            color={theme.palette.text.primary}
            sx={{
              flexGrow: 1,
            }}
          >
            {clients?.count ?? 0} {t('clients.results')}
          </Typography>
          <SearchInput placeholder={t('clients.search')} onSearch={handleOnSearch} />

          <ToggleButtonGroup
            exclusive
            sx={{
              height: theme.spacing(5),
              '& button': {
                padding: theme.spacing(1),
                width: theme.spacing(5),
              },
            }}
          >
            <ToggleButton onClick={toggleCreate} value="add" aria-label={t('clients.create.title')}>
              <Add />
            </ToggleButton>
          </ToggleButtonGroup>

        </Box>
        <CreateClientModal open={createClientmodal} onClose={toggleCreate} />

        {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}

        {clients && <Table
          selectedItemId={selectedItemId}
          onRowSelect={onSelect}
          columns={[
            {
              id: 'name',
              label: t('clients.table.name'),
              align: 'left',
            },
            {
              id: 'agency',
              label: t('clients.table.agency'),
              align: 'left',
            },
            { id: 'phone', label: t('clients.table.phone'), align: 'left' },
            { id: 'email', label: t('clients.table.email'), align: 'left' },
          ]}
          data={{
            key: 'email',
            items: clients?.items.map((client) => ({
              name: `${capitalize(String(client.firstName))} ${capitalize(String(client.lastName))}`,
              id: client.id,
              agency: client.agencyName,
              phone: `(${client.phoneCountryCode}) ${client.phoneNumber}`,
              email: client.email,
            })),
            totalCount: clients.count,
          }}
          pagination={pagination}
          stickyHeader
        />}

      </ModalBox>
    </Modal>
  );
};
